import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Text, UnorderedList, ListItem } from '@televet/kibble-ui';
import { ErrorType, errorDetails } from 'pages/Automations/types/AutomationValidation';
import { useAutomationEditorView } from 'pages/Automations/hooks';
import partial from 'lodash-es/partial';
import { FlattenedErrors } from 'pages/Automations/utils/validation/getFlattenedAutomationErrors';
import { promptOptionDetails } from 'pages/Automations/types/PromptOptionDetail';
import { AutomationEditorView } from 'pages/Automations/types/AutomationEditorView';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateErrorAlertIsVisible } from 'pages/Automations/state/automationsSlice';
import sortBy from 'lodash/sortBy';

interface AtuoamtionsErrorAlertProps {
  errors: FlattenedErrors;
}

export const AutomationsErrorAlert = ({ errors }: AtuoamtionsErrorAlertProps): JSX.Element => {
  const validationActionWord = useAppSelector((state) => state.automations.validationActionWord);
  const [detailsVisible, setDetailsVisible] = useState<boolean>(false);
  const { setCurrentEditorView } = useAutomationEditorView();
  const dispatch = useAppDispatch();

  const handleLinkClick = useCallback(
    (errorName: ErrorType) => {
      setCurrentEditorView(errorDetails[errorName].location);
    },
    [setCurrentEditorView],
  );

  const handleCloseAlert = useCallback(() => {
    dispatch(updateErrorAlertIsVisible(false));
  }, [dispatch]);

  const details = useMemo(() => {
    const sortedPromptErrors = sortBy(errors.promptErrors, 'promptIndex');
    return (
      <UnorderedList>
        {errors.generalErrors.map((error: ErrorType, index: number) => {
          return (
            <ListItem key={`${error}-${index}`}>
              <Text cursor="pointer" textDecoration="underline" onClick={partial(handleLinkClick, error)}>
                {errorDetails[error].formattedFieldName}:
              </Text>{' '}
              <Text>{errorDetails[error].errorMessage}</Text>
            </ListItem>
          );
        })}
        {sortedPromptErrors?.map((prompt, index) => {
          return (
            <ListItem key={`${prompt.promptType}-${index}`}>
              <Text
                cursor="pointer"
                textDecoration="underline"
                onClick={partial(setCurrentEditorView, AutomationEditorView.Compose)}
              >
                Prompt {`#${prompt.promptIndex + 1}`} - {promptOptionDetails[prompt.promptType].title}:
              </Text>{' '}
              <UnorderedList>
                {prompt.errors?.map((error, index) => (
                  <ListItem key={`${error}-${index}`}>
                    <Text>{errorDetails[error].errorMessage}</Text>
                  </ListItem>
                ))}
              </UnorderedList>
            </ListItem>
          );
        })}
      </UnorderedList>
    );
  }, [errors, setCurrentEditorView, handleLinkClick]);

  return (
    <Box position="absolute" top="75px" left={2} right={3} zIndex="banner">
      <Alert
        status="error"
        title={`Unable to ${validationActionWord} automation`}
        onClose={handleCloseAlert}
        buttonProps={{
          buttonText: detailsVisible ? 'Hide Details' : 'Show Details',
          onClick: (): void => setDetailsVisible((prevState) => !prevState),
        }}
      >
        {detailsVisible ? details : ''}
      </Alert>
    </Box>
  );
};
